import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Link as MuiLink, TextField, Typography } from '@mui/material'

import { useMfaRecoveryMutation, useMfaVerifyMutation } from '@/apis/authApi'
import { useAppDispatch } from '@/app/hooks'

import { setCredentials } from './authSlice'

const apiCode = (err: unknown) =>
    (err as { data?: { error?: { code?: string } } })?.data?.error?.code

/**
 * Second sign-in step for accounts with MFA on: a 6-digit authenticator code,
 * or one of the recovery codes. On success the session is stored and `onDone` runs.
 */
export function MfaChallengeForm({
    challengeToken,
    onDone,
    onRestart,
}: {
    challengeToken: string
    onDone: () => void
    onRestart: () => void
}) {
    const dispatch = useAppDispatch()
    const [useRecovery, setUseRecovery] = useState(false)
    const [code, setCode] = useState('')
    const [verify, { isLoading: verifying, error: verifyError }] = useMfaVerifyMutation()
    const [recover, { isLoading: recovering, error: recoverError }] = useMfaRecoveryMutation()
    const error = useRecovery ? recoverError : verifyError
    const busy = verifying || recovering
    const expired = ['INVALID_CHALLENGE', 'TOO_MANY_ATTEMPTS'].includes(apiCode(error) ?? '')

    const submit = async (e: FormEvent) => {
        e.preventDefault()
        try {
            const session = useRecovery
                ? await recover({ challengeToken, recoveryCode: code.trim() }).unwrap()
                : await verify({ challengeToken, code: code.replace(/\s+/g, '') }).unwrap()
            dispatch(setCredentials(session))
            onDone()
        } catch {
            setCode('')
        }
    }

    return (
        <Box
            component="form"
            onSubmit={submit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400, mx: 'auto' }}
        >
            <Typography variant="h4" textAlign="center">
                Two-step verification
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
                {useRecovery
                    ? 'Enter one of the recovery codes you saved when you turned on MFA. Each code works once.'
                    : 'Enter the 6-digit code from your authenticator app.'}
            </Typography>
            {error && (
                <Alert
                    severity="error"
                    action={
                        expired ? (
                            <Button color="inherit" size="small" onClick={onRestart}>
                                Sign in again
                            </Button>
                        ) : undefined
                    }
                >
                    {expired
                        ? 'This sign-in attempt has expired or had too many tries.'
                        : useRecovery
                          ? 'That recovery code is not valid.'
                          : 'That code is not valid. Check the time on your phone and try again.'}
                </Alert>
            )}
            <TextField
                label={useRecovery ? 'Recovery code' : 'Authentication code'}
                value={code}
                onChange={e => setCode(e.target.value)}
                required
                autoFocus
                fullWidth
                autoComplete="one-time-code"
                inputProps={
                    useRecovery
                        ? { maxLength: 64 }
                        : { inputMode: 'numeric', pattern: '[0-9 ]{6,7}', maxLength: 7 }
                }
            />
            <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy || expired}
                fullWidth
            >
                {busy ? 'Checking…' : 'Verify'}
            </Button>
            <Typography variant="body2" textAlign="center">
                <MuiLink
                    component="button"
                    type="button"
                    onClick={() => {
                        setUseRecovery(r => !r)
                        setCode('')
                    }}
                >
                    {useRecovery ? 'Use your authenticator app' : 'Use a recovery code instead'}
                </MuiLink>
            </Typography>
        </Box>
    )
}
